import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { getParticipantById, getParticipantDisplayName, isPrivateChatEnabled } from '../../../base/participants/functions';
import Popover from '../../../base/popover/components/Popover.web';
import { getDisplayNameSuffix, getFormattedTimestamp, getPrivateNoticeMessage, } from '../../functions';
import { withPixelLineHeight } from "../../../base/styles/functions.web";
import { useMemo } from "react";
import { Attachment } from "../../../../11sight/features/chat/components/attachments";
import { DownloadButton } from "../../../../11sight/features/chat/components/attachments/components/DownloadButton";
import Message from "../../../base/react/components/web/Message";
import { MESSAGE_TYPE_LOCAL } from "../../constants";
import { getMessageText } from "../../functions";
import PrivateMessageButton from "./PrivateMessageButton";
const useStyles = makeStyles()((theme, _, classes) => {
    const borderRadius = theme.spacing(2);
    return {
        chatMessageWrapper: {
            maxWidth: "100%",
            "&.local": {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end"
            }
        },
        chatMessage: {
            display: "inline-flex",
            maxWidth: "100%",
            marginTop: "4px",
            boxSizing: "border-box",
            color: theme.palette.iisightGray700,
            "&.privatemessage": {
                backgroundColor: theme.palette.support05
            },
            "&.local": {
                ".userMessage": {
                    backgroundColor: theme.palette.iisightGray100,
                    borderRadius: `${borderRadius} 0 ${borderRadius} ${borderRadius}`
                },
                "&.privatemessage": {
                    backgroundColor: theme.palette.support05
                }
            },
            "&.error": {
                borderRadius: 0,
                fontWeight: 100,
                [`& ${classes.userMessage}`]: {
                    backgroundColor: "rgb(215, 121, 118)"
                }
            },
            "&.lobbymessage": {
                backgroundColor: theme.palette.support05
            }
        },
        replyWrapper: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            maxWidth: "100%"
        },
        messageContent: {
            maxWidth: "100%",
            overflow: "visible",
            flex: 1
        },
        replyButtonContainer: {
            display: "flex",
            alignItems: "center",
            height: "100%",
            position: "absolute",
            top: "50%",
            right: "-35px",
            transform: "translateY(-50%)"
        },
        replyButton: {
            padding: "2px"
        },
        displayName: {
            ...withPixelLineHeight(theme.typography.labelBold),
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            marginBottom: theme.spacing(1)
        },
        userMessage: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            width: "fit-content",
            wordBreak: "break-word",
            padding: "12px",
            backgroundColor: theme.palette.iisightGreen100,
            borderRadius: `0 ${borderRadius} ${borderRadius} ${borderRadius}`,
            position: "relative",
            overflow: "visible"
        },
        privateMessageNotice: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text02,
            marginTop: theme.spacing(1)
        },
        timestamp: {
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.iisightGray400,
            marginTop: theme.spacing(1)
        },
        attachmentContainer: {
            height: "min-content",
            position: "relative",
            maxWidth: 190
        }
    };
});
const ChatMessage = ({ className = '', message, state, showDisplayName, shouldDisplayMenuOnRight, enablePrivateChat, knocking, t, showTimestamp }) => {
    const { classes, cx } = useStyles();
    const [isHovered, setIsHovered] = useState(false);
    const [isReactionsOpen, setIsReactionsOpen] = useState(false);
    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);
    const handleReactionsOpen = useCallback(() => {
        setIsReactionsOpen(true);
    }, []);
    const handleReactionsClose = useCallback(() => {
        setIsReactionsOpen(false);
    }, []);
    /**
     * Renders the display name of the sender.
     *
     * @returns {React$Element<*>}
     */
    function _renderDisplayName() {
        const { displayName } = message;
        return (React.createElement("div", { "aria-hidden": true, className: cx('display-name', classes.displayName) }, `${displayName}${getDisplayNameSuffix(message)}`));
    }
    /**
     * Renders the message privacy notice.
     *
     * @returns {React$Element<*>}
     */
    function _renderPrivateNotice() {
        return (React.createElement("div", { className: classes.privateMessageNotice }, getPrivateNoticeMessage(message)));
    }
    /**
     * Renders the time at which the message was sent.
     *
     * @returns {React$Element<*>}
     */
    function _renderTimestamp() {
        return (React.createElement("div", { className: cx('timestamp', classes.timestamp) },
            React.createElement("p", null, getFormattedTimestamp(message))));
    }
    /**
     * Renders the reactions for the message.
     *
     * @returns {React$Element<*>}
     */
    const renderReactions = useMemo(() => {
        if (!message.reactions || message.reactions.size === 0) {
            return null;
        }
        const reactionsArray = Array.from(message.reactions.entries())
            .map(([reaction, participants]) => {
            return { reaction,
                participants };
        })
            .sort((a, b) => b.participants.size - a.participants.size);
        const totalReactions = reactionsArray.reduce((sum, { participants }) => sum + participants.size, 0);
        const numReactionsDisplayed = 3;
        const reactionsContent = (React.createElement("div", { className: classes.reactionsPopover }, reactionsArray.map(({ reaction, participants }) => (React.createElement("div", { className: classes.reactionItem, key: reaction },
            React.createElement("p", null,
                React.createElement("span", null, reaction),
                React.createElement("span", null, participants.size)),
            React.createElement("div", { className: classes.participantList }, Array.from(participants).map(participantId => (React.createElement("p", { className: classes.participant, key: participantId }, state && getParticipantDisplayName(state, participantId))))))))));
        return (React.createElement(Popover, { content: reactionsContent, onPopoverClose: handleReactionsClose, onPopoverOpen: handleReactionsOpen, position: 'top', trigger: 'hover', visible: isReactionsOpen },
            React.createElement("div", { className: classes.reactionBox },
                reactionsArray.slice(0, numReactionsDisplayed).map(({ reaction }, index) => React.createElement("p", { key: index }, reaction)),
                reactionsArray.length > numReactionsDisplayed && (React.createElement("p", { className: classes.reactionCount },
                    "+",
                    totalReactions - numReactionsDisplayed)))));
    }, [message?.reactions, isHovered, isReactionsOpen]);
    let attachment = useMemo(() => {
        try {
            const parsedMessage = JSON.parse(message.message);
            if (typeof parsedMessage !== "object")
                return null;
            else
                return parsedMessage;
        }
        catch (err) {
            return null;
        }
    }, []);
    return ((React.createElement("div", { className: cx(classes.chatMessageWrapper, className), id: message.messageId, tabIndex: -1 },
        React.createElement("div", { className: cx("attachment-container", "chatmessage", classes.chatMessage, className) },
            React.createElement("div", { className: classes.replyWrapper },
                React.createElement("div", { className: cx("messagecontent", classes.messageContent) },
                    showDisplayName && _renderDisplayName(),
                    React.createElement("div", { className: cx("usermessage", classes.userMessage) },
                        React.createElement("span", { className: "sr-only" }, message.displayName === message.recipient ? t("chat.messageAccessibleTitleMe") : t("chat.messageAccessibleTitle", {
                            user: message.displayName
                        })),
                        attachment ? (React.createElement("div", { className: classes.attachmentContainer },
                            React.createElement(Attachment, { ...attachment }),
                            React.createElement(DownloadButton, { name: attachment.name, src: attachment.url }))) : (React.createElement(Message, { text: getMessageText(message) })),
                        (message.privateMessage || (message.lobbyChat && !knocking)) && message.messageType !== MESSAGE_TYPE_LOCAL && (React.createElement("div", { className: classes.replyButtonContainer },
                            React.createElement(PrivateMessageButton, { isLobbyMessage: message.lobbyChat, participantID: message.id }))))))),
        (message.privateMessage || (message.lobbyChat && !knocking)) && _renderPrivateNotice(),
        showTimestamp && _renderTimestamp())));
};
/**
 * Maps part of the Redux store to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
function _mapStateToProps(state, { message }) {
    const { knocking } = state['features/lobby'];
    const participant = getParticipantById(state, message.participantId);
    // For visitor private messages, participant will be undefined but we should still allow private chat
    // Create a visitor participant object for visitor messages to pass to isPrivateChatEnabled
    const participantForCheck = message.isFromVisitor
        ? { id: message.participantId, name: message.displayName, isVisitor: true }
        : participant;
    const enablePrivateChat = (!message.isFromVisitor || message.privateMessage)
        && isPrivateChatEnabled(participantForCheck, state);
    // Only the local messages appear on the right side of the chat therefore only for them the menu has to be on the
    // left side.
    const shouldDisplayMenuOnRight = message.messageType !== MESSAGE_TYPE_LOCAL;
    return {
        shouldDisplayMenuOnRight,
        enablePrivateChat,
        knocking,
        state
    };
}
export default translate(connect(_mapStateToProps)(ChatMessage));
